import React, { useContext } from 'react';

import { ThemeContext } from 'styled-components';
import { getBreakpointFromManor } from 'ctm-components';

import { Typography } from '@comparethemarketau/manor-typography';
import { Picture } from '@comparethemarketau/manor-picture';
import { Link } from '@comparethemarketau/manor-link';
import { Row, Column, Container } from '@comparethemarketau/manor-grid';

import { getAssetFilePath } from '../../summary/common/get_asset_file_path';

const getKobaHandoverWizardPages = function (productDisclosures) {
    const theme = useContext(ThemeContext);

    const pdsObject = productDisclosures && productDisclosures.find((element) => element.get('code') === 'PDS');

    const pdsUrl = pdsObject && pdsObject.get('url');

    const breakpoint = getBreakpointFromManor();

    let titleStyle = { fontSize: theme.fontSize['3xl'], fontWeight: theme.fontWeight.semibold };
    let contentPaddingSpace = ['28', '84', '28', '84'];
    if (breakpoint === 'xs') {
        contentPaddingSpace = ['24', '36', '24', '36'];
        titleStyle = { fontSize: theme.fontSize.xl, fontWeight: theme.fontWeight.semibold };
    } else if (breakpoint === 'sm') {
        contentPaddingSpace = ['28', '60', '28', '64'];
        titleStyle = { fontSize: theme.fontSize['3xl'], fontWeight: theme.fontWeight.semibold };
    }

    return [
        <Container padding={contentPaddingSpace} key={'page-1'}>
            <Row>
                <Column valign="center" halign="center">
                    <Picture title={'contract'} src={getAssetFilePath('/assets/images/logos/car-insurance/contract-info.svg')} />
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        KOBA’s pay-per-km insurance
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        KOBA offers the same type of comprehensive car insurance as other insurers; but with a new way to pay.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        To use the features of this product, you will need to download KOBA's smartphone app and plug a KOBA Rider (a matchbox-sized device) into your car.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        This device will be sent to you when you buy a policy.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-2'}>
            <Row>
                <Column valign="center" halign="center">
                    <Picture title={'payment'} src={getAssetFilePath('/assets/images/logos/car-insurance/payment.svg')} />
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Understanding your quote
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        You pay for cover with KOBA in the following way:
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        {' '}
                        <strong>An upfront fixed cost</strong>
                        {' '}
                        which covers your car while it’s not in use and a small Admin fee. Both are refundable on a pro-rata basis, if the policy is cancelled during the term;
                        {' '}
                        <strong>PLUS</strong>
                        {' '}
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        {' '}
                        <strong>A Per-km rate</strong>
                        {' '}
                        which covers you while driving and is used to calculate your monthly Driving premium which is payable at the end of
                        each month based on the total kilometres driven as measured through your KOBA Rider.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-3'}>
            <Row>
                <Column valign="center" halign="center">
                    <Picture title={'driver app'} src={getAssetFilePath('/assets/images/logos/car-insurance/driver-app.svg')} />
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        How It Works
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        The KOBA Rider securely measures the number of kilometres you drive; the less you drive each month the lower the premium.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        Through your App, you will be able to see real time trips, monthly costs, claims information and policy documents.
                    </Typography>
                </Column>
            </Row>
        </Container>,
        <Container padding={contentPaddingSpace} key={'page-4'}>
            <Row>
                <Column valign="center" halign="center">
                    <Picture title={'reminders'} src={getAssetFilePath('/assets/images/logos/car-insurance/reminders.svg')} />
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" style={titleStyle}>
                        Additional information
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        You have 14 days from your policy start date to plug in your KOBA Rider.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        KOBA policies rely on the information from the KOBA Rider so if you don’t plug in the Rider device within 14 days,
                        you may be charged for the maximum estimated daily kilometres of 250km per day.
                    </Typography>
                </Column>
            </Row>
            <Row>
                <Column valign="center" halign="center">
                    <Typography variant="body1" align="center">
                        For more details about this product please refer to
                        {' '}
                        <Link trackingLabel="PDS link" href={pdsUrl} target="_blank" underline="none">
                            Product Disclosure Statement
                        </Link>
                        {' '}
                        &
                        {' '}
                        <Link trackingLabel="PEG link" href="https://www.kobainsurance.com.au/TMD" target="_blank" underline="none">
                            Target Market Determination
                        </Link>
                        {' '}
                    </Typography>
                </Column>
            </Row>
        </Container>
    ];
};

export { getKobaHandoverWizardPages };
